/** Обёртка поля TanStack Form: рендерит контрол и ошибку валидации. */
import type {
	AnyFieldApi,
	DeepKeys,
	FormAsyncValidateOrFn,
	FormValidateOrFn,
	ReactFormExtendedApi,
} from "@tanstack/react-form";
import type { ReactNode } from "react";
import { FormTextError } from "./FormTextError";

type FormApi<
	TData,
	TOnMount extends FormValidateOrFn<TData> | undefined,
	TOnChange extends FormValidateOrFn<TData> | undefined,
	TOnChangeAsync extends FormAsyncValidateOrFn<TData> | undefined,
	TOnBlur extends FormValidateOrFn<TData> | undefined,
	TOnBlurAsync extends FormAsyncValidateOrFn<TData> | undefined,
	TOnSubmit extends FormValidateOrFn<TData> | undefined,
	TOnSubmitAsync extends FormAsyncValidateOrFn<TData> | undefined,
	TOnDynamic extends FormValidateOrFn<TData> | undefined,
	TOnDynamicAsync extends FormAsyncValidateOrFn<TData> | undefined,
	TOnServer extends FormAsyncValidateOrFn<TData> | undefined,
	TSubmitMeta,
> = ReactFormExtendedApi<
	TData,
	TOnMount,
	TOnChange,
	TOnChangeAsync,
	TOnBlur,
	TOnBlurAsync,
	TOnSubmit,
	TOnSubmitAsync,
	TOnDynamic,
	TOnDynamicAsync,
	TOnServer,
	TSubmitMeta
>;

type FormFieldProps<
	TData,
	TOnMount extends FormValidateOrFn<TData> | undefined,
	TOnChange extends FormValidateOrFn<TData> | undefined,
	TOnChangeAsync extends FormAsyncValidateOrFn<TData> | undefined,
	TOnBlur extends FormValidateOrFn<TData> | undefined,
	TOnBlurAsync extends FormAsyncValidateOrFn<TData> | undefined,
	TOnSubmit extends FormValidateOrFn<TData> | undefined,
	TOnSubmitAsync extends FormAsyncValidateOrFn<TData> | undefined,
	TOnDynamic extends FormValidateOrFn<TData> | undefined,
	TOnDynamicAsync extends FormAsyncValidateOrFn<TData> | undefined,
	TOnServer extends FormAsyncValidateOrFn<TData> | undefined,
	TSubmitMeta,
	TName extends DeepKeys<TData>,
> = {
	form: FormApi<
		TData,
		TOnMount,
		TOnChange,
		TOnChangeAsync,
		TOnBlur,
		TOnBlurAsync,
		TOnSubmit,
		TOnSubmitAsync,
		TOnDynamic,
		TOnDynamicAsync,
		TOnServer,
		TSubmitMeta
	>;
	name: TName;
	/** Ошибка поверх разметки: поле не резервирует место под неё (поля в тулбаре) */
	compact?: boolean;
	children: (field: AnyFieldApi) => ReactNode;
};

export const FormField = <
	TData,
	TOnMount extends FormValidateOrFn<TData> | undefined,
	TOnChange extends FormValidateOrFn<TData> | undefined,
	TOnChangeAsync extends FormAsyncValidateOrFn<TData> | undefined,
	TOnBlur extends FormValidateOrFn<TData> | undefined,
	TOnBlurAsync extends FormAsyncValidateOrFn<TData> | undefined,
	TOnSubmit extends FormValidateOrFn<TData> | undefined,
	TOnSubmitAsync extends FormAsyncValidateOrFn<TData> | undefined,
	TOnDynamic extends FormValidateOrFn<TData> | undefined,
	TOnDynamicAsync extends FormAsyncValidateOrFn<TData> | undefined,
	TOnServer extends FormAsyncValidateOrFn<TData> | undefined,
	TSubmitMeta,
	TName extends DeepKeys<TData>,
>(
	props: FormFieldProps<
		TData,
		TOnMount,
		TOnChange,
		TOnChangeAsync,
		TOnBlur,
		TOnBlurAsync,
		TOnSubmit,
		TOnSubmitAsync,
		TOnDynamic,
		TOnDynamicAsync,
		TOnServer,
		TSubmitMeta,
		TName
	>,
) => {
	const Field = props.form.Field;
	return (
		<Field name={props.name}>
			{(field) => (
				<div className={`m-0 flex flex-col ${props.compact ? "relative" : ""}`}>
					{props.children(field)}
					<FormTextError errors={field.state.meta.errors} floating={props.compact} />
				</div>
			)}
		</Field>
	);
};
